import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MipsDataService } from "@/lib/mips-data-service";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Award } from "lucide-react";
import BenchmarkChart from "@/components/gap-management/shared/BenchmarkChart";

export interface MeasureBasic {
  id: string;
  title: string;
}

interface AddMeasureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  measures: MeasureBasic[];
  onAdd: (ids: string[]) => void;
}

export default function QualityMeasureSelector({ open, onOpenChange, measures, onAdd }: AddMeasureModalProps) {
  const [query, setQuery] = useState("");
  const [collectionType, setCollectionType] = useState<string>("all");
  const [hideTopped, setHideTopped] = useState(false);
  const [onlyAuto7, setOnlyAuto7] = useState(false);
  const [hideCap, setHideCap] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const dataService = MipsDataService.getInstance();
    return measures
      .map((m) => {
        const [measureId, submissionMethod] = m.id.split(':');
        const measureData = dataService.getMeasureWithSubmissionMethod(measureId, submissionMethod);
        if (!measureData) {
          const meta = dataService.getMeasureMeta(measureId);
          const deciles = dataService.getDecilesForMeasure(measureId);
          return { ...m, ...meta, deciles, measureId } as any;
        }
        const deciles = measureData.deciles || [];
        return {
          id: m.id,
          title: m.title,
          measureId,
          collectionType: measureData.submissionMethod,
          inverse: measureData.isInverse,
          autoPts: measureData.autoPts || false,
          scoringFloor: measureData.scoringFloor,
          cmsAvg: measureData.cmsAverage,
          cap7: measureData.sevenPointCap,
          toppedOut: measureData.toppedOut,
          deciles
        } as any;
      })
      .filter((r: any) => {
        if (r.collectionType === 'Unknown') return false;
        return (
          (query.trim() === "" || `${r.id} ${r.title}`.toLowerCase().includes(query.toLowerCase())) &&
          (collectionType === "all" || r.collectionType === collectionType) &&
          (!hideTopped || !r.toppedOut) &&
          (!onlyAuto7 || r.autoPts) &&
          (!hideCap || !r.cap7)
        );
      });
  }, [measures, query, collectionType, hideTopped, onlyAuto7, hideCap]);

  const allChecked = rows.length > 0 && rows.every((r: any) => selected.has(r.id));
  const someChecked = rows.some((r: any) => selected.has(r.id)) && !allChecked;

  const toggleAll = (checked: boolean) => {
    const next = new Set(selected);
    if (checked) rows.forEach((r: any) => next.add(r.id));
    else rows.forEach((r: any) => next.delete(r.id));
    setSelected(next);
  };

  const toggleOne = (id: string, checked: boolean) => {
    const next = new Set(selected);
    checked ? next.add(id) : next.delete(id);
    setSelected(next);
  };

  const addSelected = () => {
    onAdd(Array.from(selected));
    setSelected(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setSelected(new Set()); onOpenChange(o); }}>
      <DialogContent className="max-w-6xl h-[90vh]">
        <DialogHeader>
          <DialogTitle>Add Quality Measures</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Input placeholder="Search measures..." value={query} onChange={(e) => setQuery(e.target.value)} className="w-64" />
          <Select value={collectionType} onValueChange={setCollectionType}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent className="z-50 bg-background">
              <SelectItem value="all">All Collection Types</SelectItem>
              <SelectItem value="MIPS CQM">MIPS CQM</SelectItem>
              <SelectItem value="QCDR">QCDR</SelectItem>
              <SelectItem value="eCQM">eCQM</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={hideTopped} onCheckedChange={(v: any) => setHideTopped(!!v)} /> Hide topped-out measures
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={onlyAuto7} onCheckedChange={(v: any) => setOnlyAuto7(!!v)} /> Show Bonus Pts Measures
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={hideCap} onCheckedChange={(v: any) => setHideCap(!!v)} /> Hide measures capped at 7 pts
          </label>
        </div>

        <div className="mt-4 rounded-lg border max-h-[420px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox checked={rows.length === 0 ? false : allChecked ? true : someChecked ? "indeterminate" : false as any} onCheckedChange={() => toggleAll(!allChecked)} />
                </TableHead>
                <TableHead className="text-center">ID</TableHead>
                <TableHead className="text-center">Collection Type</TableHead>
                <TableHead className="text-center">Title</TableHead>
                <TableHead className="text-center">Inverse</TableHead>
                <TableHead className="text-center">Auto Pts</TableHead>
                <TableHead className="text-center">CMS Avg</TableHead>
                <TableHead className="text-center">7pt Cap</TableHead>
                <TableHead className="min-w-[400px] text-center">Performance Thresholds</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={(v: any) => toggleOne(r.id, !!v)} />
                  </TableCell>
                  <TableCell className="font-medium">{r.measureId || r.id}</TableCell>
                  <TableCell>{r.collectionType}</TableCell>
                  <TableCell className="min-w-[280px]">{r.title}</TableCell>
                  <TableCell>{r.inverse ? "Yes" : "No"}</TableCell>
                  <TableCell>{r.autoPts ? "Yes" : "No"}</TableCell>
                  <TableCell>{r.cmsAvg !== null ? `${r.cmsAvg.toFixed(2)}%` : '--'}</TableCell>
                  <TableCell>{r.cap7 ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {r.autoPts && (!r.deciles || r.deciles.length === 0) ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-muted-foreground">No benchmarks available</span>
                          <div className="bg-primary text-primary-foreground px-3 py-1.5 rounded-md flex items-center justify-center gap-2 font-medium text-xs">
                            <Award className="h-3 w-3" />
                            <span>+{r.scoringFloor === '7_Points' ? '7' : '5'} BONUS POINTS AWARDED</span>
                            <Award className="h-3 w-3" />
                          </div>
                        </div>
                      ) : r.deciles && r.deciles.length > 0 ? (
                        <div className="space-y-1">
                          {/* Quick threshold callouts (match prior UI) */}
                          <div className="text-xs font-mono">
                            {[2, 4, 6, 9].map((index) => {
                              const decile = r.deciles[index];
                              if (!decile) return null;
                              const threshold = r.inverse ? decile.to : decile.from;
                              const fmt = (v: number | null) => (v === null ? '--' : `${v.toFixed(2)}%`);
                              return (
                                <span key={index}>
                                  D{index + 1}: {r.inverse ? '≤' : '≥'}{fmt(threshold)}{index < 9 && ' | '}
                                </span>
                              );
                            }).filter(Boolean)}
                          </div>

                          {/* CMS average label */}
                          <div className="text-[11px] text-muted-foreground">CMS: {r.cmsAvg !== null && r.cmsAvg !== undefined ? `${r.cmsAvg.toFixed(1)}%` : '--'}</div>

                          {/* Segmented decile bar with CMS marker (prior style) */}
                          <div className="relative h-4 w-full min-w-[320px] bg-gray-200 rounded overflow-hidden">
                            {(() => {
                              const cmsAvg = r.cmsAvg ?? 50;
                              const segments = r.deciles.map((d: any, i: number) => {
                                const isNotAchievable = d.from === null && d.to === null;
                                let width = 10;
                                if (d.from !== null && d.to !== null && !isNotAchievable) {
                                  width = Math.abs(d.to - d.from);
                                } else if (i === 0 && d.to !== null && !isNotAchievable) {
                                  width = r.inverse ? d.to : 100 - d.to;
                                } else if (i === 9 && d.from !== null && !isNotAchievable) {
                                  width = r.inverse ? 100 - d.from : d.from;
                                }
                                let color = '';
                                if (isNotAchievable) {
                                  color = 'bg-gray-400';
                                } else {
                                  if (i === 0) color = 'bg-red-600';
                                  else if (i === 1) color = 'bg-red-500';
                                  else if (i === 2) color = 'bg-red-400';
                                  else if (i === 3) color = 'bg-orange-500';
                                  else if (i === 4) color = 'bg-orange-400';
                                  else if (i === 5) color = 'bg-yellow-500';
                                  else if (i === 6) color = 'bg-yellow-400';
                                  else if (i === 7) color = 'bg-green-400';
                                  else if (i === 8) color = 'bg-green-500';
                                  else color = 'bg-green-600';
                                }
                                return { width, color };
                              });
                              const totalWidth = segments.reduce((s: number, seg: any) => s + seg.width, 0) || 1;
                              const cmsPos = r.inverse ? (100 - cmsAvg) : cmsAvg;
                              return (
                                <TooltipProvider>
                                  <div className="absolute inset-0 flex">
                                    {segments.map((seg: any, i: number) => {
                                      const percentWidth = (seg.width / totalWidth) * 100;
                                      const d = r.deciles[i];
                                      const fmt = (v: number | null) => (v === null ? '--' : `${v.toFixed(2)}%`);
                                      return (
                                        <Tooltip key={i}>
                                          <TooltipTrigger asChild>
                                            <div className={`${seg.color}`} style={{ width: `${percentWidth}%` }} />
                                          </TooltipTrigger>
                                          <TooltipContent className="z-50">
                                            <div className="text-xs">D{i + 1}: {fmt(d.from)} – {fmt(d.to)}</div>
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  </div>
                                  <div className="absolute inset-y-0" style={{ left: `${cmsPos}%` }} title={`CMS Average: ${cmsAvg.toFixed(1)}%`}>
                                    <div className="h-full w-0.5 bg-black" />
                                    <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2">
                                      <span className="text-[10px] font-bold bg-white/95 px-1 rounded shadow-sm">CMS</span>
                                    </div>
                                  </div>
                                </TooltipProvider>
                              );
                            })()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">No measures found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DialogFooter className="flex items-center justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={addSelected} disabled={selected.size === 0}>Add Selected</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



